import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

interface TimeEntry {
  datum: string;
  tagname: string;
  projektnummer: string;
  projektname: string;
  bestellnummer: string;
  worktype: string;
  task: string;
  beschreibung: string;
  stunden: number;
  billable: boolean;
}

interface DetailedTimeEntriesTableProps {
  entries: TimeEntry[];
  selectedMonth: number | null;
  selectedProject: string | null;
  year: number;
}

export function DetailedTimeEntriesTable({ 
  entries, 
  selectedMonth, 
  selectedProject, 
  year 
}: DetailedTimeEntriesTableProps) {
  const [isExpanded, setIsExpanded] = useState(true);

  const filteredEntries = entries.filter(entry => {
    if (selectedMonth !== null) {
      if (!entry.datum) return false;
      const entryDate = new Date(entry.datum.split('.').reverse().join('-'));
      if (isNaN(entryDate.getTime()) || entryDate.getMonth() !== selectedMonth) {
        return false;
      }
    }
    
    if (selectedProject !== null) {
      if (entry.projektnummer !== selectedProject) {
        return false;
      }
    }
    
    return true;
  });

  const totalHours = filteredEntries.reduce((sum, entry) => sum + entry.stunden, 0);
  const billableHours = filteredEntries.reduce((sum, entry) => entry.billable ? sum + entry.stunden : sum, 0);

  const getFilterDescription = () => {
    const monthNames = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'];
    
    if (selectedMonth !== null && selectedProject !== null) {
      return `${monthNames[selectedMonth]} ${year} - ${selectedProject}`;
    } else if (selectedMonth !== null) {
      return `${monthNames[selectedMonth]} ${year}`;
    } else if (selectedProject !== null) {
      return `Projekt ${selectedProject}`;
    } else {
      return `Year to Date ${year}`;
    }
  };

  return (
    <div className="ov-bg-white ov-rounded-lg ov-p-6 ov-border ov-border-gray-200">
      <div 
        className="ov-flex ov-justify-between ov-items-center ov-mb-4 ov-cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="ov-flex ov-items-center ov-gap-3">
          <h3 className="ov-text-gray-900 ov-font-bold">
            Detaillierte Zeiterfassung ({getFilterDescription()})
          </h3>
          <div className="ov-text-sm ov-text-gray-600">
            {filteredEntries.length} {filteredEntries.length === 1 ? 'Eintrag' : 'Einträge'}
          </div>
        </div>
        <button className="ov-text-gray-500 ov-hover:text-gray-700 ov-bg-transparent ov-border-none ov-outline-none">
          {isExpanded ? <ChevronUp className="ov-w-5 ov-h-5" /> : <ChevronDown className="ov-w-5 ov-h-5" />}
        </button>
      </div>

      {isExpanded && (
        <div 
          className="ov-overflow-x-auto ov-max-h-600px ov-overflow-y-auto ov-pr-1"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 #f1f5f9'
          }}
        >
          <table className="ov-w-full">
            <thead className="ov-sticky ov-top-0 ov-bg-white ov-z-10">
              <tr className="ov-border-b ov-border-gray-200">
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Datum</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Tagname</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Projekt-ID</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Projektname</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Bestellnummer</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Worktype</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Task</th>
                <th className="ov-text-left ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Beschreibung</th>
                <th className="ov-text-right ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Stunden</th>
                <th className="ov-text-center ov-py-3 ov-px-2 ov-text-gray-600 ov-text-sm">Billable</th>
              </tr>
            </thead>
            <tbody>
              {filteredEntries.map((entry, index) => (
                <tr key={index} className="ov-border-b ov-border-gray-100 ov-hover:bg-gray-50">
                  <td className="ov-py-3 ov-px-2 ov-text-gray-900 ov-text-sm">{entry.datum}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-700 ov-text-sm">{entry.tagname}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-900 ov-text-sm ov-font-mono">{entry.projektnummer}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-700 ov-text-sm">{entry.projektname}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-700 ov-text-sm ov-font-mono">{entry.bestellnummer}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-700 ov-text-sm">{entry.worktype}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-700 ov-text-sm">{entry.task}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-gray-700 ov-text-sm">{entry.beschreibung}</td>
                  <td className="ov-py-3 ov-px-2 ov-text-right ov-text-gray-900">{entry.stunden}h</td>
                  <td className="ov-py-3 ov-px-2 ov-text-center">
                    <span className={`ov-inline-block ov-px-2 ov-py-1 ov-rounded ov-text-xs ${
                      entry.billable 
                        ? 'ov-bg-green-100 ov-text-green-700' 
                        : 'ov-bg-red-100 ov-text-red-700'
                    }`}>
                      {entry.billable ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="ov-sticky ov-bottom-0 ov-bg-white ov-z-10">
              <tr className="ov-border-t-2 ov-border-gray-300 ov-bg-gray-50">
                <td colSpan={8} className="ov-py-3 ov-px-2 ov-text-gray-900">Gesamt</td>
                <td className="ov-py-3 ov-px-2 ov-text-right ov-text-gray-900">{totalHours.toFixed(2)}h</td>
                <td className="ov-py-3 ov-px-2 ov-text-center">
                  <span className="ov-inline-block ov-px-2 ov-py-1 ov-bg-blue-100 ov-text-blue-700 ov-rounded ov-text-xs">
                    {totalHours > 0 ? ((billableHours / totalHours) * 100).toFixed(0) : 0}%
                  </span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

